/**
 * Size drawer for the product page variant picker.
 *
 * The size option renders as a compact trigger plus a native <dialog>, so the
 * option list stays short on mobile while the underlying radio inputs stay in
 * place inside the .option-selector fieldset. variant-picker.js keeps working
 * unchanged because it reads the checked input straight from the fieldset.
 *
 * Dependencies: none (native <dialog>).
 */
if (!customElements.get('pk-size-drawer')) {
  class PkSizeDrawer extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-drawer-trigger]');
      this.dialog = this.querySelector('[data-drawer-dialog]');
      this.valueEl = this.querySelector('[data-drawer-value]');
      if (!this.trigger || !this.dialog) return;

      this.placeholder = this.valueEl
        ? this.valueEl.dataset.placeholder || this.valueEl.textContent.trim()
        : '';

      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onDialogChange = this.onDialogChange.bind(this);
      this.onDialogClose = this.onDialogClose.bind(this);

      this.trigger.addEventListener('click', this.onTriggerClick);
      this.dialog.addEventListener('click', this.onDialogClick);
      this.dialog.addEventListener('change', this.onDialogChange);
      this.dialog.addEventListener('close', this.onDialogClose);
    }

    disconnectedCallback() {
      if (!this.trigger) return;
      clearTimeout(this.closeTimer);
      this.trigger.removeEventListener('click', this.onTriggerClick);
      this.dialog.removeEventListener('click', this.onDialogClick);
      this.dialog.removeEventListener('change', this.onDialogChange);
      this.dialog.removeEventListener('close', this.onDialogClose);
      document.body.classList.remove('pk-size-drawer-open');
    }

    get isOpen() {
      return this.dialog.hasAttribute('open');
    }

    get selectedInput() {
      return this.dialog.querySelector('input.js-option:checked');
    }

    open() {
      if (this.isOpen) return;
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      document.body.classList.add('pk-size-drawer-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.syncValue();

      const target = this.selectedInput
        || this.dialog.querySelector('input.js-option:not(.is-unavailable)')
        || this.dialog.querySelector('input.js-option');
      if (target) target.focus({ preventScroll: true });
    }

    close() {
      if (!this.isOpen) return;
      // Clean up synchronously: the queued 'close' event can be delayed in throttled tabs.
      document.body.classList.remove('pk-size-drawer-open');
      this.trigger.setAttribute('aria-expanded', 'false');

      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
    }

    onTriggerClick(evt) {
      evt.preventDefault();
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    onDialogClick(evt) {
      // Clicking the backdrop targets the dialog element itself.
      if (evt.target === this.dialog) {
        this.close();
        return;
      }

      const closeBtn = evt.target.closest('[data-drawer-close]');
      if (closeBtn && this.contains(closeBtn)) {
        evt.preventDefault();
        this.close();
        return;
      }

      // Re-tapping the already selected size fires no 'change' event, so close here too.
      const label = evt.target.closest('.opt-label');
      if (label && this.dialog.contains(label)) {
        this.scheduleClose();
      }
    }

    onDialogChange(evt) {
      if (!evt.target.matches('input.js-option')) return;
      this.syncValue();
      this.scheduleClose();
    }

    scheduleClose() {
      clearTimeout(this.closeTimer);
      // Let the selection paint before the panel slides away.
      this.closeTimer = setTimeout(() => this.close(), 140);
    }

    onDialogClose() {
      document.body.classList.remove('pk-size-drawer-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.focus({ preventScroll: true });
    }

    syncValue() {
      if (!this.valueEl) return;
      const input = this.selectedInput;
      this.valueEl.textContent = input ? input.value : this.placeholder;
    }
  }

  customElements.define('pk-size-drawer', PkSizeDrawer);
}
